// What the reader is looking at, in a form that survives a link and a visit.
//
// Three things used to be lost the moment somebody closed the tab: where the
// map was, which layers they had on, and any way to hand either to another
// person. The viewport is MapLibre's own `hash` option and needs nothing from
// this file. The layer selection is here, because it is the part with a
// judgement in it.
//
// The judgement is that a selection is stored as a DEVIATION from the
// defaults, not as the whole state. `?on=cameras&off=vessels` means "whatever
// this build ships, plus cameras, minus ships". The alternative — listing
// every visible layer — is shorter to write and wrong in a way that only shows
// up months later: a layer added after a link was shared would be absent from
// the list, so every old link would silently switch off a feature the reader
// had never chosen to hide. Deviation-encoding means a new layer arrives at
// its default for everyone, which is the only answer that stays true as the
// map grows.
//
// The same choice keeps URLs short. Sixteen providers and four vehicle kinds
// would otherwise be twenty tokens in every shared link; in practice a reader
// deviates from the defaults in one or two ways, so most links carry one
// parameter or none at all.
//
// Precedence, when more than one source has an opinion: URL, then storage,
// then defaults. A shared link has to show what the sharer saw or it is not a
// shared link — somebody's own saved habits must not rewrite a view they were
// sent. Absent a link, a returning reader gets their habits back.

/** Mover kinds and provider ids share one flat namespace in the URL. */
export interface Selection {
  /** Ids switched ON that the defaults would have had off. */
  readonly on: readonly string[];
  /** Ids switched OFF that the defaults would have had on. */
  readonly off: readonly string[];
}

/** What this build ships with, before the reader touches anything. */
export interface Defaults {
  /** Every mover kind, and whether it starts on. */
  readonly kinds: Readonly<Record<string, boolean>>;
  /** Provider ids that start hidden — off by design, or not configured here. */
  readonly hiddenProviders: ReadonlySet<string>;
  /** Every provider id this place's layer tree named. */
  readonly knownProviders: ReadonlySet<string>;
}

/** The reader's actual state, in the shape main.ts already keeps it. */
export interface ViewState {
  readonly kinds: Record<string, boolean>;
  readonly hiddenProviders: Set<string>;
}

export const STORAGE_KEY = 'abz.layers';

/**
 * Ids that mean two different things at once.
 *
 * The URL namespace is flat: `?off=vessels` is a mover kind and `?off=cameras`
 * is a provider, and nothing in the token says which. That is fine today —
 * checked, not assumed — because no provider in any place's layer tree is
 * called aircraft, vessels, buses or trains. It would stop being fine the day
 * somebody registers a provider with one of those ids, and the failure would
 * be a shared link quietly toggling the wrong thing rather than an error.
 *
 * So the collision is a thing a test asks about, against the real registry,
 * for every place. See scripts/test-view-state.mjs.
 */
export function namespaceCollisions(defaults: Defaults): string[] {
  return [...defaults.knownProviders].filter((id) => id in defaults.kinds).sort();
}

/** The reader's state, expressed as what they changed. */
export function selectionFor(defaults: Defaults, state: ViewState): Selection {
  const on: string[] = [];
  const off: string[] = [];

  for (const [kind, isDefaultOn] of Object.entries(defaults.kinds)) {
    const isOn = state.kinds[kind] !== false;
    if (isOn && !isDefaultOn) on.push(kind);
    if (!isOn && isDefaultOn) off.push(kind);
  }

  for (const id of defaults.knownProviders) {
    const isOn = !state.hiddenProviders.has(id);
    const isDefaultOn = !defaults.hiddenProviders.has(id);
    if (isOn && !isDefaultOn) on.push(id);
    if (!isOn && isDefaultOn) off.push(id);
  }

  // Sorted so the same view always produces the same URL. Two readers looking
  // at the same thing should be able to compare links, and a link that
  // reshuffles its own parameters on every toggle looks broken.
  return { on: on.sort(), off: off.sort() };
}

/** Defaults, with a selection laid over them. */
export function applySelection(defaults: Defaults, selection: Selection): ViewState {
  const kinds: Record<string, boolean> = { ...defaults.kinds };
  const hiddenProviders = new Set(defaults.hiddenProviders);

  // Unknown ids are ignored rather than rejected. A link shared from a newer
  // build, or an older one, names layers this build may not have — and the
  // right answer is to honour the parts that do exist rather than to discard
  // the whole selection over a token nobody recognises.
  for (const id of selection.on) {
    if (id in kinds) kinds[id] = true;
    else if (defaults.knownProviders.has(id)) hiddenProviders.delete(id);
  }
  for (const id of selection.off) {
    if (id in kinds) kinds[id] = false;
    else if (defaults.knownProviders.has(id)) hiddenProviders.add(id);
  }

  return { kinds, hiddenProviders };
}

/** Nothing to say — the reader is looking at what everybody else starts with. */
export const isDefaultView = (s: Selection): boolean => s.on.length === 0 && s.off.length === 0;

const splitList = (raw: string | null): string[] => (raw ?? '')
  .split(',')
  .map((s) => s.trim())
  .filter(Boolean);

/** Read a selection out of a query string. Never throws. */
export function parseSelection(search: string): Selection {
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(search);
  } catch {
    return { on: [], off: [] };
  }
  // `off` wins a token that appears in both, because switching something off
  // is the more conservative reading of a contradictory link.
  const off = splitList(params.get('off'));
  const offSet = new Set(off);
  return { on: splitList(params.get('on')).filter((id) => !offSet.has(id)), off };
}

/**
 * The query string for a selection, preserving anything already there.
 *
 * `?marker=` is the reason this merges rather than replaces: a FUBAR share
 * link deep-links to one report, and a reader who then toggles a layer must
 * not have the marker dropped out from under them.
 *
 * Returns the search string including its leading `?`, or '' for a default
 * view — an unadorned URL is the right thing to leave in the address bar when
 * there is nothing to say, and the right thing to hand somebody.
 */
export function selectionToSearch(selection: Selection, existing = ''): string {
  const params = new URLSearchParams(existing);
  params.delete('on');
  params.delete('off');
  if (selection.on.length) params.set('on', selection.on.join(','));
  if (selection.off.length) params.set('off', selection.off.join(','));
  // URLSearchParams percent-encodes the separator, giving `off=rivers%2Ctides`.
  // That is correct and it is ugly, and on a feature whose entire output is a
  // URL somebody will paste into a message, legible beats pedantic. A comma is
  // a legal sub-delimiter in a query value (RFC 3986) and reads back
  // identically — `new URLSearchParams('a=x,y').get('a')` is 'x,y' — so this
  // is a display choice with no parsing consequence, including for any other
  // parameter that happens to contain one.
  const query = params.toString().replace(/%2C/g, ',');
  return query ? `?${query}` : '';
}

/** Somewhere to keep a selection. Passed in so tests need no browser. */
export interface SelectionStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * The reader's last selection, or null.
 *
 * Every access is guarded: storage throws outright in some privacy modes
 * rather than returning null, and a map that fails to start because somebody
 * browses privately would be a poor trade for remembering a checkbox.
 */
export function readStored(store: SelectionStore): Selection | null {
  try {
    const raw = store.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const { on, off } = parsed as { on?: unknown; off?: unknown };
    const list = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x) => typeof x === 'string') : []);
    return { on: list(on), off: list(off) };
  } catch {
    return null;
  }
}

/** Keep a selection, or forget it once the reader is back to the defaults. */
export function writeStored(store: SelectionStore, selection: Selection): void {
  try {
    if (isDefaultView(selection)) store.removeItem(STORAGE_KEY);
    else store.setItem(STORAGE_KEY, JSON.stringify({ on: selection.on, off: selection.off }));
  } catch {
    // Full, or refused. The map works; the preference does not persist.
  }
}

/**
 * Which selection to open with: the link, then the habit, then the defaults.
 *
 * Split out from the wiring because the precedence is the whole decision, and
 * it is the sort of rule that gets quietly inverted by a later edit unless
 * something asserts it.
 */
export function openingSelection(search: string, store: SelectionStore | null): Selection {
  const fromUrl = parseSelection(search);
  if (!isDefaultView(fromUrl)) return fromUrl;
  return (store && readStored(store)) ?? { on: [], off: [] };
}
